/**
 * Clubs API
 *
 * Handles club management, membership, and settings.
 */

import { apiGet, apiPost, apiPut, apiPatch, apiDelete, API_BASE_URL } from '../api';
import { guardedFetch } from '../accountGuard';
import { apiUpload, type UploadRequestOptions, type ApiRequestOptions } from '../apiTransport';
import type { UploadableImagePart } from './images';
import type { CheckinMethod } from './events';

// Re-exported for consumers of the club-hero upload surface (#2105) — the
// method signatures below take this type, so callers (native hook + tests)
// can import it from here without reaching into './images'.
export type { UploadableImagePart } from './images';

// External Link Types (#758, #1109 added youtube, #1184 removed strava)
export type ExternalLinkType = 'website' | 'instagram' | 'facebook' | 'youtube' | 'custom';

export interface ExternalLink {
  type: ExternalLinkType;
  url: string;
  label?: string | null;
}

// Subscription tier values per docs/canonical/business/subscription-tiers.md
export type SubscriptionTier = 'free' | 'essential' | 'advanced';

// Meeting mode for clubs (#635 - Virtual clubs)
export type MeetingMode = 'in_person' | 'virtual' | 'hybrid';

// Region for virtual clubs (#635)
export type ClubRegion = 'americas' | 'emea' | 'apac';

export interface Club {
  id: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
  hero_image_url: string | null;
  avatar_image_url: string | null; // Issue #562 - Dedicated square avatar
  meeting_mode?: MeetingMode;
  city: string;
  state: string;
  location?: string; // Combined city/state or full location string
  region?: ClubRegion | null;
  primary_sport?: string;
  secondary_sports?: string[];
  subscription_level: string | null; // Legacy field
  subscription_tier?: SubscriptionTier; // Issue #760 - Canonical tier field
  subscription_expires_at?: number | null; // Issue #760 - Unix timestamp, null = permanent
  clean_language_enabled?: boolean; // Issue #793 - Per-club profanity filter toggle
  numbering_enabled?: boolean;
  next_event_number?: number;
  timezone?: string; // IANA tz, used for event time rendering
  slug: string;
  owner_id: string;
  visibility: 'public' | 'private' | 'members_only';
  invite_permission?: 'all_members' | 'leaders_only' | 'disabled'; // #870
  created_at: number; // Unix seconds
}

export interface ClubWithRole extends Club {
  role: 'owner' | 'leader' | 'moderator' | 'member';
  /** True when clubs.owner_id = this user. Role alone cannot answer ownership:
   *  creators are inserted with role 'leader'; only a transfer writes 'owner'
   *  (#2176 trap, re-hit on #2339). Computed server-side in GET /clubs. */
  is_owner: boolean;
  member_count: number;
  upcoming_event_count: number;
}

export interface ClubDetailResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    hero_image_url: string | null;
    avatar_image_url: string | null; // Issue #562
    meeting_mode?: MeetingMode;
    city?: string;
    state?: string;
    location?: string; // Combined city/state or full location
    region?: ClubRegion | null;
    primary_sport?: string;
    secondary_sports?: string[];
    visibility?: 'public' | 'private' | 'members_only';
    subscription_level: string | null; // Legacy field
    subscription_tier?: SubscriptionTier; // Issue #760
    subscription_expires_at?: number | null; // Issue #760
    clean_language_enabled?: number; // Issue #793 - Per-club profanity filter (0/1 from SQLite)
    numbering_enabled?: number; // 0/1 from SQLite
    next_event_number?: number;
    timezone?: string; // IANA tz, used for event time rendering
    invite_permission?: 'all_members' | 'leaders_only' | 'disabled'; // #870
    owner_id: string;
    created_at: number;
    updated_at: number;
  };
  stats: {
    member_count: number;
    upcoming_event_count: number;
  };
  membership: {
    role: 'owner' | 'leader' | 'moderator' | 'member' | 'none';
    is_owner: boolean;
  };
  external_links: ExternalLink[]; // #758
}

// Discovery filter params (#635, #1355)
export interface DiscoverClubsParams {
  sport?: string;
  meeting_mode?: 'in_person' | 'virtual' | 'hybrid';
  region?: ClubRegion;
}

// Discoverable club (from discover endpoint)
export interface DiscoverableClub {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  hero_image_url: string | null;
  avatar_image_url: string | null;
  visibility: 'public' | 'members_only';
  meeting_mode: MeetingMode;
  region: ClubRegion | null;
  primary_sport: string | null;
  created_at: number;
  member_count: number;
}

export interface CreateClubPayload {
  name: string;
  description?: string;
  meeting_mode?: MeetingMode;
  city?: string;
  state?: string;
  location?: string;
  region?: ClubRegion | null;
  cover_image_url?: string;
  primary_sport?: string;
  timezone?: string; // IANA tz; frontend defaults to browser tz
}

export interface UpdateClubPayload {
  name?: string;
  // #2960: clearable fields accept an explicit `null` (clears the column).
  // `undefined` = untouched (key dropped by JSON.stringify); `null` = clear.
  description?: string | null;
  meeting_mode?: MeetingMode;
  city?: string | null;
  state?: string | null;
  region?: ClubRegion | null;
  cover_image_url?: string;
  primary_sport?: string | null;
  secondary_sports?: string[] | null;
  subscription_level?: string;
  visibility?: 'public' | 'private' | 'members_only';
  invite_permission?: 'all_members' | 'leaders_only' | 'disabled'; // #870
  external_links?: ExternalLink[]; // #758
  clean_language_enabled?: boolean; // #793
  numbering_enabled?: boolean; // #1509
  next_event_number?: number; // #1509
  timezone?: string; // IANA tz; needed by future Settings override UI
}

export interface UpdateClubResponse {
  club: Club;
  external_links: ExternalLink[];
}

export interface TransferOwnershipPayload {
  new_owner_id: string;
  old_owner_role?: 'leader' | 'member' | 'leave';
}

/**
 * The club shape `POST /clubs` actually returns (#2107 contract audit).
 *
 * The 201 handler re-SELECTs a NARROW column set (backend/src/routes/clubs.ts
 * "Fetch the created club"): id, name, slug, description, hero_image_url,
 * avatar_image_url, owner_id, created_at, updated_at — NOT the full `Club`
 * this response was typed as until #2107. The idempotent duplicate-window
 * path (same slug + owner within 5 min) instead returns `SELECT *` with a
 * 200 — a superset — so this narrow type is truthful for both statuses.
 * Consumers (web CreateClubPage/CreateClubStep, native create screen) read
 * only `id` and `slug`.
 */
export interface CreatedClub {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  hero_image_url: string | null;
  avatar_image_url: string | null;
  owner_id: string;
  created_at: number;
  updated_at: number;
}

export interface CreateClubResponse {
  club: CreatedClub;
}

// Membership Request Types (#782)
export interface MembershipRequest {
  id: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: number;
  reviewed_at?: number | null;
}

export interface MembershipRequestResponse {
  request: MembershipRequest | null;
}

// Pending membership request with user info (#1239)
export interface PendingMembershipRequest {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'denied';
  source: string | null;
  requested_at: number;
  reviewed_at: number | null;
  reviewed_by: string | null;
  display_name: string;
  avatar_url: string | null;
}

export interface PendingMembershipRequestsResponse {
  requests: PendingMembershipRequest[];
}

export interface ReviewMembershipRequestPayload {
  action: 'approve' | 'deny';
}

export interface ReviewMembershipRequestResponse {
  request: {
    id: string;
    status: 'approved' | 'denied';
    reviewed_at: number;
    reviewed_by: string;
  };
}

export interface AdminClubDetailResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    city: string;
    state: string;
    visibility: 'public' | 'members_only' | 'private';
    subscription_level: string;
    hero_image_url: string | null;
    created_at: number; // Unix seconds
    member_count: number;
  };
  owner: {
    id: string;
    email: string;
    public_name: string;
    location: string | null;
    created_at: number; // Unix seconds
  } | null;
}

/**
 * Response envelope from `POST /upload/club-hero-image/:clubId`
 * (`backend/src/routes/upload.ts:419`). NOTE: this is the dedicated club-hero
 * handler's shape — `{ success, image_url, message }` — NOT `imagesApi`'s
 * generic `{ image: { url, type } }`. Pinned to the real handler (Rule 55).
 */
export interface ClubHeroUploadResponse {
  success: boolean;
  image_url: string;
  message: string;
}

/** Response envelope from `DELETE /upload/club-hero-image/:clubId` (`upload.ts:466`). */
export interface ClubHeroRemoveResponse {
  success: boolean;
  message: string;
}

/**
 * Response envelope from `POST /upload/club-avatar/:clubId`
 * (`backend/src/routes/upload.ts:547-551`, #562/#2356). Like the hero pair
 * above, this is the dedicated club-avatar handler's shape —
 * `{ success, image_url, message }` — NOT `imagesApi`'s generic
 * `{ image: { url, type } }`. Pinned to the real handler (Rule 55).
 */
export interface ClubAvatarUploadResponse {
  success: boolean;
  image_url: string;
  message: string;
}

/** Response envelope from `DELETE /upload/club-avatar/:clubId` (`upload.ts:595-598`). */
export interface ClubAvatarRemoveResponse {
  success: boolean;
  message: string;
}

export const clubsApi = {
  /**
   * List all clubs for current user
   * GET /clubs
   */
  listMyClubs: () => apiGet<ClubWithRole[]>('/clubs'),

  /**
   * Discover public clubs with optional filters
   * GET /clubs/discover
   * #635, #1355: Supports sport, meeting_mode, and region filters
   */
  discoverClubs: (params?: DiscoverClubsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.sport) searchParams.set('sport', params.sport);
    if (params?.meeting_mode) searchParams.set('meeting_mode', params.meeting_mode);
    if (params?.region) searchParams.set('region', params.region);
    const queryString = searchParams.toString();
    return apiGet<DiscoverableClub[]>(`/clubs/discover${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * List all clubs on platform (Platform Admin only)
   * GET /clubs/admin/clubs
   */
  listAllClubs: () => apiGet<{ clubs: ClubWithRole[] }>('/clubs/admin/clubs'),

  /**
   * Get club details for platform admin (metadata + owner info only)
   * GET /clubs/admin/clubs/:slug
   */
  getClubForAdmin: (slug: string) => apiGet<AdminClubDetailResponse>(`/clubs/admin/clubs/${slug}`),

  /**
   * Get single club by slug
   * GET /clubs/:slug
   */
  getClub: (slug: string) => apiGet<ClubDetailResponse>(`/clubs/${slug}`),

  /**
   * Get single club by ID
   * GET /clubs/id/:id
   */
  getClubById: (id: string) => apiGet<ClubDetailResponse>(`/clubs/id/${id}`),

  /**
   * Create new club
   * POST /clubs (201; idempotent duplicate-window replays return 200)
   *
   * `options` (idempotency key / abort signal) pass-through added for the
   * native create-club screen (#2107), matching the `sendRoomMessage`
   * precedent. Additive: web's useCreateClub calls apiPost directly and the
   * one web call site (onboarding CreateClubStep) passes no options.
   */
  createClub: (payload: CreateClubPayload, options?: ApiRequestOptions) =>
    apiPost<CreateClubResponse>('/clubs', payload, options),

  /**
   * Update club details
   * PUT /clubs/:slug
   */
  updateClub: (slug: string, payload: UpdateClubPayload) =>
    apiPut<UpdateClubResponse>(`/clubs/${slug}`, payload),

  /**
   * Delete club
   * DELETE /clubs/:slug
   */
  deleteClub: (slug: string) => apiDelete<void>(`/clubs/${slug}`),

  /**
   * Join a club
   * POST /clubs/:slug/join
   */
  joinClub: (slug: string) => apiPost<void>(`/clubs/${slug}/join`),

  /**
   * Leave a club
   * POST /clubs/:slug/leave
   */
  leaveClub: (slug: string) => apiPost<void>(`/clubs/${slug}/leave`),

  /**
   * Transfer club ownership
   * POST /clubs/:slug/transfer
   */
  transferOwnership: (slug: string, payload: TransferOwnershipPayload) =>
    apiPost<void>(`/clubs/${slug}/transfer`, payload),

  /**
   * Upload club hero image
   * POST /upload/club-hero-image/:clubId
   */
  uploadClubHeroImage: async (clubId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await guardedFetch(`${API_BASE_URL}/upload/club-hero-image/${clubId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Failed to upload hero image');
    }
    
    return response.json();
  },

  /**
   * Remove club hero image
   * DELETE /upload/club-hero-image/:clubId
   */
  removeClubHeroImage: async (clubId: string) => {
    const response = await guardedFetch(`${API_BASE_URL}/upload/club-hero-image/${clubId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Removal failed' }));
      throw new Error(error.message || 'Failed to remove hero image');
    }

    return response.json();
  },

  /**
   * Upload a club hero image through the platform transport (#2105).
   *
   * Unlike `uploadClubHeroImage` above (a pre-transport web-only path: raw
   * fetch + cookie session, mirrors `imagesApi.upload`), this rides `apiUpload`
   * — so on native it gets Bearer auth, X-Client, one-shot 401 refresh-retry,
   * upload progress, and cancel; on web it gets the cookie session. Both hit
   * the SAME dedicated endpoint (leader/owner-gated, deletes the old R2 object,
   * persists `clubs.hero_image_url`, returns the CF-transform URL).
   *
   * The multipart field is `image` only — the handler reads no `type` field
   * (`backend/src/routes/upload.ts:363`); the variant is fixed server-side.
   *
   * POST /upload/club-hero-image/:clubId
   *
   * @param part - Web File/Blob or RN `{ uri, name, type }` descriptor
   */
  uploadClubHeroAsset: (
    clubId: string,
    part: UploadableImagePart,
    options?: UploadRequestOptions,
  ): Promise<ClubHeroUploadResponse> => {
    const formData = new FormData();
    // RN's descriptor isn't a Blob in the TS lib's eyes but is accepted by
    // RN's FormData at runtime; the cast is confined to this one append.
    formData.append('image', part as Blob);
    return apiUpload<ClubHeroUploadResponse>(`/upload/club-hero-image/${clubId}`, formData, options);
  },

  /**
   * Remove a club hero image through the platform transport (#2105).
   * Transport sibling of `removeClubHeroImage` above.
   *
   * DELETE /upload/club-hero-image/:clubId
   */
  removeClubHeroAsset: (
    clubId: string,
    options?: ApiRequestOptions,
  ): Promise<ClubHeroRemoveResponse> =>
    apiDelete<ClubHeroRemoveResponse>(`/upload/club-hero-image/${clubId}`, options),

  /**
   * Upload a club avatar through the platform transport (#2356).
   *
   * Transport sibling of `uploadClubHeroAsset` above, hitting the DEDICATED
   * club-avatar endpoint (#562) — NOT the generic `/images/upload` (which
   * returns a URL and persists nothing). The handler is leader/owner-gated,
   * deletes the old R2 object, persists `clubs.avatar_image_url`, and returns
   * the CF-transform URL (256×256 fit=cover, `buildOptimizedImageUrl(...,
   * 'avatar')`). On native this rides `apiUpload` (Bearer auth, X-Client,
   * one-shot 401 refresh-retry, upload progress, cancel); on web the cookie
   * session.
   *
   * The multipart field is `image` only — the handler reads no `type` field
   * (`backend/src/routes/upload.ts:495`); the variant is fixed server-side.
   *
   * POST /upload/club-avatar/:clubId  (club.id UUID, not slug)
   *
   * @param part - Web File/Blob or RN `{ uri, name, type }` descriptor
   */
  uploadClubAvatarAsset: (
    clubId: string,
    part: UploadableImagePart,
    options?: UploadRequestOptions,
  ): Promise<ClubAvatarUploadResponse> => {
    const formData = new FormData();
    // RN's descriptor isn't a Blob in the TS lib's eyes but is accepted by
    // RN's FormData at runtime; the cast is confined to this one append.
    formData.append('image', part as Blob);
    return apiUpload<ClubAvatarUploadResponse>(`/upload/club-avatar/${clubId}`, formData, options);
  },

  /**
   * Remove a club avatar through the platform transport (#2356).
   * Clears `clubs.avatar_image_url` (display falls back to the cover photo).
   *
   * DELETE /upload/club-avatar/:clubId
   */
  removeClubAvatarAsset: (
    clubId: string,
    options?: ApiRequestOptions,
  ): Promise<ClubAvatarRemoveResponse> =>
    apiDelete<ClubAvatarRemoveResponse>(`/upload/club-avatar/${clubId}`, options),

  // ============================================================================
  // MEMBERSHIP REQUESTS (#782)
  // ============================================================================

  /**
   * Request to join an invite-only club
   * POST /clubs/:clubId/membership-requests
   */
  requestMembership: (clubId: string) =>
    apiPost<{ request: MembershipRequest }>(`/clubs/${clubId}/membership-requests`),

  /**
   * Get current user's membership request for a club
   * GET /clubs/:clubId/membership-requests/me
   */
  getMyMembershipRequest: (clubId: string) =>
    apiGet<MembershipRequestResponse>(`/clubs/${clubId}/membership-requests/me`),

  /**
   * List pending membership requests for a club (leaders only)
   * GET /clubs/:clubId/membership-requests
   * Issue #1239
   */
  getPendingMembershipRequests: (clubId: string) =>
    apiGet<PendingMembershipRequestsResponse>(`/clubs/${clubId}/membership-requests`),

  /**
   * Approve or deny a membership request (leaders only)
   * PATCH /clubs/:clubId/membership-requests/:requestId
   * Issue #1239
   */
  reviewMembershipRequest: (clubId: string, requestId: string, payload: ReviewMembershipRequestPayload) =>
    apiPatch<ReviewMembershipRequestResponse>(`/clubs/${clubId}/membership-requests/${requestId}`, payload),

  // ============================================================================
  // ADMIN SUBSCRIPTION MANAGEMENT (#842)
  // ============================================================================

  /**
   * Update a club's subscription tier (Platform Admin only)
   * PUT /clubs/admin/clubs/:id/subscription
   */
  updateSubscription: (clubId: string, payload: UpdateSubscriptionPayload) =>
    apiPut<UpdateSubscriptionResponse>(`/clubs/admin/clubs/${clubId}/subscription`, payload),

  // ============================================================================
  // CLUB EVENT DEFAULTS (#172 - v1.2)
  // ============================================================================

  /**
   * Get club's event defaults configuration
   * GET /clubs/:slug/event-defaults
   * Leaders+ only
   */
  getEventDefaults: (slug: string) =>
    apiGet<ClubEventDefaultsResponse>(`/clubs/${slug}/event-defaults`),

  /**
   * Update club's event defaults configuration
   * PUT /clubs/:slug/event-defaults
   * Leaders+ only
   */
  updateEventDefaults: (slug: string, payload: UpdateClubEventDefaultsPayload) =>
    apiPut<{ ok: true }>(`/clubs/${slug}/event-defaults`, payload),
};

// Admin subscription update types (#842)
export interface UpdateSubscriptionPayload {
  tier: SubscriptionTier;
  expires_at?: number | null;
}

export interface UpdateSubscriptionResponse {
  club: {
    id: string;
    subscription_tier: SubscriptionTier;
    subscription_expires_at: number | null;
  };
}

// Club Event Defaults types (#172 - v1.2)
// Note: CheckinMethod is exported from events.ts to avoid duplicate export

export interface ClubEventDefaults {
  default_start_time: string | null;
  default_duration_minutes: number | null;
  default_location_name: string | null;
  default_location_address: string | null;
  default_location_lat: number | null;
  default_location_lng: number | null;
  default_visibility: 'public' | 'members_only' | null;
  default_description_template: string | null;
  default_check_ins_enabled: boolean | null;
  default_checkin_method: CheckinMethod | null;
  default_checkin_radius_m: number | null;
  default_checkin_window_before_min: number | null;
  default_checkin_window_after_min: number | null;
  default_is_virtual: boolean | null;
}

export interface ClubEventDefaultsResponse {
  event_defaults: ClubEventDefaults | null;
}

export interface UpdateClubEventDefaultsPayload {
  default_start_time?: string | null;
  default_duration_minutes?: number | null;
  default_location_name?: string | null;
  default_location_address?: string | null;
  default_location_lat?: number | null;
  default_location_lng?: number | null;
  default_visibility?: 'public' | 'members_only' | null;
  default_description_template?: string | null;
  default_check_ins_enabled?: boolean | null;
  default_checkin_method?: CheckinMethod | null;
  default_checkin_radius_m?: number | null;
  default_checkin_window_before_min?: number | null;
  default_checkin_window_after_min?: number | null;
  default_is_virtual?: boolean | null;
}
